import type { IAPIRoutes } from "../../../../config";
import type {
  NeoConversationDTO,
  INeoConversationRepository,
  NeoConversationMessageDTO,
  NeoConversationStreamFrame,
  NeoConversationToolCallDTO,
} from "../../../core/ports/driven/neoConversationRepository";

const env = import.meta.env;

const apiBaseUrl = (): string =>
  env.VITE_API_GATEWAY_URL_V2 || env.VITE_API_GATEWAY_URL || "";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string =>
  Object.entries(params).reduce(
    (route, [key, value]) => route.replace(`{${key}}`, String(value)),
    template
  );

const parseFrame = (raw: string): NeoConversationStreamFrame | null => {
  let event = "message";
  const dataLines: string[] = [];

  for (const line of raw.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0) {
    return raw.trim().startsWith(":")
      ? { event: "keepalive", data: null }
      : null;
  }

  const rawData = dataLines.join("\n");
  try {
    const parsed = JSON.parse(rawData);
    return {
      event,
      data:
        parsed && typeof parsed === "object"
          ? (parsed as Record<string, unknown>)
          : { value: parsed },
    };
  } catch {
    return { event, data: { text: rawData } };
  }
};

const drainSSE = async (
  response: Response,
  onFrame: (frame: NeoConversationStreamFrame) => void,
  signal: AbortSignal
): Promise<void> => {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() ?? "";
      frames.forEach((raw) => {
        const frame = parseFrame(raw);
        if (frame) onFrame(frame);
      });
    }

    buffer += decoder.decode();
    const trailing = parseFrame(buffer);
    if (trailing) onFrame(trailing);
  } finally {
    reader.releaseLock();
  }
};

export const NeoConversationRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoConversationRepository => {
  const listConversationsAPI: INeoConversationRepository["listConversationsAPI"] =
    async () =>
      APIProvider.GET(APIRoutes.apiNeoConversations, {
        limit: 50,
        include_project_conversations: false,
      });

  const listConversationActivityAPI: INeoConversationRepository["listConversationActivityAPI"] =
    async (params) =>
      APIProvider.GET(APIRoutes.apiNeoConversationActivity, params);

  const markConversationViewedAPI: INeoConversationRepository["markConversationViewedAPI"] =
    async (conversationId, throughSequenceNum) =>
      APIProvider.PUT(
        fillRoute(APIRoutes.apiNeoConversationViewed, {
          conversation_id: conversationId,
        }),
        { through_sequence_num: throughSequenceNum }
      );

  const getConversationHistoryAPI: INeoConversationRepository["getConversationHistoryAPI"] =
    async (conversationId) => {
      const [conversation, messagesResponse, toolsResponse] = await Promise.all(
        [
          APIProvider.GET(
            fillRoute(APIRoutes.apiNeoConversation, {
              conversation_id: conversationId,
            }),
            {}
          ),
          APIProvider.GET(
            fillRoute(APIRoutes.apiNeoMessages, {
              conversation_id: conversationId,
            }),
            {}
          ),
          APIProvider.GET(
            fillRoute(APIRoutes.apiNeoConversationToolCalls, {
              conversation_id: conversationId,
            }),
            {}
          ),
        ]
      );
      return {
        conversation: conversation as NeoConversationDTO,
        messages:
          (messagesResponse as { items?: NeoConversationMessageDTO[] })
            ?.items ?? [],
        toolCalls:
          (toolsResponse as { items?: NeoConversationToolCallDTO[] })?.items ??
          [],
      };
    };

  const updateConversationTitleAPI: INeoConversationRepository["updateConversationTitleAPI"] =
    async (conversationId, title) =>
      APIProvider.PATCH(
        fillRoute(APIRoutes.apiNeoConversation, {
          conversation_id: conversationId,
        }),
        { title }
      );

  const createConversationAPI: INeoConversationRepository["createConversationAPI"] =
    async (payload) => APIProvider.POST(APIRoutes.apiNeoConversations, payload);

  const deleteConversationAPI: INeoConversationRepository["deleteConversationAPI"] =
    async (conversationId) =>
      // Returns the raw body: the provider resolves (not throws) a non-2xx
      // response, so the service validates it.
      APIProvider.DELETE(
        fillRoute(APIRoutes.apiNeoConversation, {
          conversation_id: conversationId,
        }),
        {}
      );

  const uploadFileAPI: INeoConversationRepository["uploadFileAPI"] = async (
    conversationId,
    file
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("conversation_id", String(conversationId));
    const token = await AuthService.getAuthenticationToken();
    const response = await fetch(apiBaseUrl() + APIRoutes.apiNeoFiles, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const error = new Error(
        `Neo file upload failed with HTTP ${response.status}`
      ) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return response.json();
  };

  const streamTurnAPI: INeoConversationRepository["streamTurnAPI"] = async (
    conversationId,
    payload,
    onFrame,
    signal
  ) => {
    const [token, accessToken] = await Promise.all([
      AuthService.getAuthenticationToken(),
      AuthService.getAuthenticationAccessToken(),
    ]);
    const response = await fetch(
      apiBaseUrl() +
        fillRoute(APIRoutes.apiNeoConversationTurnStream, {
          conversation_id: conversationId,
        }),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-CDL-Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(payload),
        signal,
      }
    );

    if (!response.ok) {
      const error = new Error(
        `Neo turn failed with HTTP ${response.status}`
      ) as Error & { status?: number; code?: string; kind?: string };
      error.status = response.status;
      // FastAPI errors carry ``detail: { error_code, kind, message }``; keep
      // the codes so the UI can tell "trial ended" from "out of credits".
      try {
        const body = await response.json();
        const detail =
          body && typeof body.detail === "object" ? body.detail : body;
        if (typeof detail?.error_code === "string") {
          error.code = detail.error_code;
        }
        if (typeof detail?.kind === "string") error.kind = detail.kind;
      } catch {
        /* non-JSON error body */
      }
      throw error;
    }

    await drainSSE(response, onFrame, signal);
  };

  const cancelMessageAPI: INeoConversationRepository["cancelMessageAPI"] =
    async (messageId, cause) => {
      await APIProvider.POST(
        `${fillRoute(APIRoutes.apiNeoMessageCancel, {
          message_id: messageId,
        })}?cause=${cause}`,
        {}
      );
    };

  return {
    listConversationsAPI,
    listConversationActivityAPI,
    markConversationViewedAPI,
    getConversationHistoryAPI,
    updateConversationTitleAPI,
    createConversationAPI,
    deleteConversationAPI,
    uploadFileAPI,
    streamTurnAPI,
    cancelMessageAPI,
  };
};
